import React, { useState } from 'react';
import { 
  UserIcon, 
  PlusIcon,
  TrashIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Mission, MissionPatient, Priority } from '../../types';
import { debugLog } from '../../utils/debug';
import PatientDeleteModal from './PatientDeleteModal';
import clsx from 'clsx';

interface PatientTabProps {
  mission: Mission;
  onUpdate: (updates: Partial<Mission>) => void;
  onAddPatient: (patient: Partial<MissionPatient>) => void;
  onUpdatePatient: (patientId: string, updates: Partial<MissionPatient>) => void;
  onRemovePatient: (patientId: string) => void;
  canEdit: boolean;
}

const PatientTab: React.FC<PatientTabProps> = ({ 
  mission, 
  onUpdate, 
  onAddPatient,
  onUpdatePatient,
  onRemovePatient,
  canEdit 
}) => {
  const [newPatient, setNewPatient] = useState<Partial<MissionPatient>>({});
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [localPatientValues, setLocalPatientValues] = useState<Record<string, Partial<MissionPatient>>>({});
  const [localMissionValues, setLocalMissionValues] = useState<Record<string, any>>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<MissionPatient | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingPatient, setIsAddingPatient] = useState(false);
  const [addPatientError, setAddPatientError] = useState<string | null>(null);

  // Debug logging
  React.useEffect(() => {
    debugLog('PatientTab: mission updated', {
      patientCount: mission.patientCount,
      patientsArrayLength: mission.patients?.length,
      patients: mission.patients
    });
  }, [mission]);

  const handleAddPatient = async () => {
    debugLog('PatientTab: handleAddPatient called', {
      newPatient,
      currentPatientCount: mission.patientCount,
      currentPatientsLength: mission.patients?.length
    });
    
    setIsAddingPatient(true);
    setAddPatientError(null);
    try {
      await onAddPatient({
        ...newPatient,
        patientNumber: (mission.patients?.length || 0) + 1
      });
      debugLog('PatientTab: Patient added successfully');
      setNewPatient({});
      setShowAddPatient(false);
    } catch (error: any) {
      // Keep form open on error
      console.error('Failed to add patient:', error);
      setAddPatientError(error.message || 'Failed to add patient. Please try again.');
    } finally {
      setIsAddingPatient(false);
    }
  };

  const handlePatientFieldChange = (patientId: string, field: keyof MissionPatient, value: any) => {
    setLocalPatientValues(prev => ({
      ...prev,
      [patientId]: {
        ...prev[patientId],
        [field]: value
      }
    }));
  };

  const handlePatientFieldBlur = (patientId: string, field: keyof MissionPatient) => {
    const patient = mission.patients?.find(p => p.id === patientId);
    const localValue = localPatientValues[patientId]?.[field];

    if (patient && localValue !== undefined && localValue !== patient[field]) {
      onUpdatePatient(patientId, { [field]: localValue });

      // Clear the local value after update
      setLocalPatientValues(prev => {
        const updated = { ...prev };
        if (updated[patientId]) {
          delete updated[patientId][field];
          if (Object.keys(updated[patientId]).length === 0) {
            delete updated[patientId];
          }
        }
        return updated;
      });
    }
  };

  const handleMissionFieldChange = (field: string, value: any) => {
    setLocalMissionValues(prev => ({ ...prev, [field]: value }));
  };

  const handleMissionFieldBlur = (field: string) => {
    const localValue = localMissionValues[field];
    if (localValue !== undefined && localValue !== (mission as any)[field]) {
      onUpdate({ [field]: localValue });
      setLocalMissionValues(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case Priority.CRITICAL:
        return 'border-red-500 bg-red-50';
      case Priority.URGENT:
        return 'border-yellow-500 bg-yellow-50';
      case Priority.ROUTINE:
        return 'border-green-500 bg-green-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  return (
    <div className="space-y-6">
      {/* Patient Count Summary */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center justify-between">
            <span className="flex items-center">
              <UserIcon className="h-5 w-5 mr-2" />
              Patient Information
            </span>
            {canEdit && (
              <button
                onClick={() => setShowAddPatient(true)}
                className="btn btn-primary btn-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Patient
              </button>
            )}
          </h3>
        </div>
        
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total Patients</span>
            <span className="text-2xl font-semibold text-gray-900">
              {mission.patients?.length ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Add New Patient Form */}
      {showAddPatient && (
        <div className="card border-2 border-blue-500">
          <div className="card-header bg-blue-50">
            <h3 className="card-title text-blue-900">Add New Patient</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Age</label>
              <input
                type="number"
                value={newPatient.age || ''}
                onChange={(e) => setNewPatient({ ...newPatient, age: parseInt(e.target.value) })}
                className="form-input"
                placeholder="Age in years"
              />
            </div>
            
            <div>
              <label className="form-label">Gender</label>
              <select
                value={newPatient.gender || ''}
                onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })}
                className="form-input"
              >
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="form-label">Weight (kg)</label>
              <input
                type="number"
                value={newPatient.weight || ''}
                onChange={(e) => setNewPatient({ ...newPatient, weight: parseInt(e.target.value) })}
                className="form-input"
                placeholder="Weight in kg"
              />
            </div>
            
            <div>
              <label className="form-label">Triage Priority</label>
              <select
                value={newPatient.triagePriority || ''}
                onChange={(e) => setNewPatient({ ...newPatient, triagePriority: e.target.value as Priority })}
                className="form-input"
              >
                <option value="">Select...</option>
                <option value={Priority.CRITICAL}>Critical</option>
                <option value={Priority.URGENT}>Urgent</option>
                <option value={Priority.ROUTINE}>Routine</option>
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Chief Complaint</label>
              <textarea
                value={newPatient.chiefComplaint || ''}
                onChange={(e) => setNewPatient({ ...newPatient, chiefComplaint: e.target.value })}
                className="form-input"
                rows={2}
                placeholder="Primary medical concern..."
              />
            </div>
          </div>
          
          {addPatientError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {addPatientError}
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowAddPatient(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAddPatient}
              className="btn btn-primary"
              disabled={isAddingPatient}
            >
              {isAddingPatient ? 'Adding...' : 'Add Patient'}
            </button>
          </div>
        </div>
      )}

      {/* Patient List */}
      {mission.patients && mission.patients.length > 0 ? (
        mission.patients.map((patient) => (
          <div 
            key={patient.id} 
            className={clsx(
              'card border-l-4',
              getPriorityColor(patient.triagePriority)
            )}
          >
            <div className="flex items-start justify-between mb-4">
              <h4 className="font-semibold text-lg">
                Patient #{patient.patientNumber}
                {patient.triagePriority && (
                  <span className={clsx(
                    'ml-2 px-2 py-0.5 rounded-full text-xs font-medium',
                    patient.triagePriority === Priority.CRITICAL && 'bg-red-100 text-red-800',
                    patient.triagePriority === Priority.URGENT && 'bg-yellow-100 text-yellow-800',
                    patient.triagePriority === Priority.ROUTINE && 'bg-green-100 text-green-800'
                  )}>
                    {patient.triagePriority}
                  </span>
                )}
              </h4>
              
              {canEdit && (
                <button
                  onClick={() => {
                    setPatientToDelete(patient);
                    setShowDeleteModal(true);
                  }}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title={`Delete Patient #${patient.patientNumber}`}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {canEdit ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Age</label>
                    <input
                      type="number"
                      value={localPatientValues[patient.id]?.age !== undefined ? localPatientValues[patient.id].age : (patient.age || '')}
                      onChange={(e) => handlePatientFieldChange(patient.id, 'age', parseInt(e.target.value) || 0)}
                      onBlur={() => handlePatientFieldBlur(patient.id, 'age')}
                      className="form-input"
                      placeholder="Age in years"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Gender</label>
                    <select
                      value={localPatientValues[patient.id]?.gender !== undefined ? localPatientValues[patient.id].gender : (patient.gender || '')}
                      onChange={(e) => {
                        const value = e.target.value;
                        handlePatientFieldChange(patient.id, 'gender', value);
                        // Direct save for selects
                        if (value !== patient.gender) {
                          onUpdatePatient(patient.id, { gender: value });
                        }
                      }}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Weight (kg)</label>
                    <input
                      type="number"
                      value={localPatientValues[patient.id]?.weight !== undefined ? localPatientValues[patient.id].weight : (patient.weight || '')}
                      onChange={(e) => handlePatientFieldChange(patient.id, 'weight', parseInt(e.target.value) || 0)}
                      onBlur={() => handlePatientFieldBlur(patient.id, 'weight')}
                      className="form-input"
                      placeholder="Weight in kg"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Triage Priority</label>
                    <select
                      value={localPatientValues[patient.id]?.triagePriority !== undefined ? localPatientValues[patient.id].triagePriority : (patient.triagePriority || '')}
                      onChange={(e) => {
                        const value = e.target.value as Priority;
                        handlePatientFieldChange(patient.id, 'triagePriority', value);
                        // Direct save for selects
                        if (value !== patient.triagePriority) {
                          onUpdatePatient(patient.id, { triagePriority: value });
                        }
                      }}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value={Priority.CRITICAL}>Critical</option>
                      <option value={Priority.URGENT}>Urgent</option>
                      <option value={Priority.ROUTINE}>Routine</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="form-label">Chief Complaint</label>
                  <textarea
                    value={localPatientValues[patient.id]?.chiefComplaint !== undefined ? localPatientValues[patient.id].chiefComplaint : (patient.chiefComplaint || '')}
                    onChange={(e) => handlePatientFieldChange(patient.id, 'chiefComplaint', e.target.value)}
                    onBlur={() => handlePatientFieldBlur(patient.id, 'chiefComplaint')}
                    className="form-input"
                    rows={2}
                    placeholder="Primary medical concern..."
                  />
                </div>
                
                <div>
                  <label className="form-label">Vital Signs</label>
                  <textarea
                    value={localPatientValues[patient.id]?.vitalSigns !== undefined ? localPatientValues[patient.id].vitalSigns : (patient.vitalSigns || '')}
                    onChange={(e) => handlePatientFieldChange(patient.id, 'vitalSigns', e.target.value)}
                    onBlur={() => handlePatientFieldBlur(patient.id, 'vitalSigns')}
                    className="form-input"
                    rows={3}
                    placeholder="BP, HR, RR, SpO2, GCS, etc..."
                  />
                </div>
                
                <div>
                  <label className="form-label">Treatment Notes</label>
                  <textarea
                    value={localPatientValues[patient.id]?.treatmentNotes !== undefined ? localPatientValues[patient.id].treatmentNotes : (patient.treatmentNotes || '')}
                    onChange={(e) => handlePatientFieldChange(patient.id, 'treatmentNotes', e.target.value)}
                    onBlur={() => handlePatientFieldBlur(patient.id, 'treatmentNotes')}
                    className="form-input"
                    rows={3}
                    placeholder="Treatment provided, medications given, etc..."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Age</p>
                  <p className="font-medium">{patient.age || 'Unknown'} years</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Gender</p>
                  <p className="font-medium">{patient.gender || 'Unknown'}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Weight</p>
                  <p className="font-medium">{patient.weight ? `${patient.weight} kg` : 'Unknown'}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Triage Priority</p>
                  <p className="font-medium">{patient.triagePriority || 'Not set'}</p>
                </div>
                
                {patient.chiefComplaint && (
                  <div className="md:col-span-3">
                    <p className="text-sm text-gray-600">Chief Complaint</p>
                    <p className="font-medium">{patient.chiefComplaint}</p>
                  </div>
                )}
                
                {patient.vitalSigns && (
                  <div className="md:col-span-3">
                    <p className="text-sm text-gray-600">Vital Signs</p>
                    <p className="font-medium whitespace-pre-line">{patient.vitalSigns}</p>
                  </div>
                )}
                
                {patient.treatmentNotes && (
                  <div className="md:col-span-3">
                    <p className="text-sm text-gray-600">Treatment Notes</p>
                    <p className="font-medium whitespace-pre-line">{patient.treatmentNotes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="card">
          <div className="text-center py-8">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No patients recorded</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add patient information as it becomes available.
            </p>
            {canEdit && (
              <div className="mt-4">
                <button
                  onClick={() => setShowAddPatient(true)}
                  className="btn btn-primary"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add First Patient
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Patient Notes */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            General Patient Notes
          </h3>
        </div>
        
        <textarea
          value={localMissionValues.patientNotes !== undefined
            ? localMissionValues.patientNotes
            : (mission.patientNotes || '')}
          onChange={(e) => handleMissionFieldChange('patientNotes', e.target.value)}
          onBlur={() => handleMissionFieldBlur('patientNotes')}
          className="form-input"
          rows={4}
          placeholder="Additional patient information, special considerations, etc..."
          disabled={!canEdit}
        />
      </div>

      {canEdit && (
        <div className="text-sm text-gray-500 italic">
          Tip: All fields auto-save when you click away or press Tab
        </div>
      )}

      {/* Patient Delete Modal */}
      <PatientDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPatientToDelete(null);
        }}
        onConfirm={async () => {
          if (patientToDelete) {
            setIsDeleting(true);
            try {
              await onRemovePatient(patientToDelete.id);
              setShowDeleteModal(false);
              setPatientToDelete(null);
            } catch (error) {
              // Error handling is done in the parent component
            } finally {
              setIsDeleting(false);
            }
          }
        }}
        patient={patientToDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PatientTab;